import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectsAPI } from '../lib/api';
import { trackEvent } from '../lib/analytics';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Loader2, 
  AlertCircle,
  Sparkles,
  Send,
  FileText,
  Target,
  AlertTriangle,
  CheckSquare,
  BarChart3,
  Calendar,
  Users,
  Clock,
  Copy,
  Check,
  ChevronDown,
  ChevronRight,
  CalendarDays,
  CheckCircle2,
  Info,
  FileSearch,
  Eye
} from 'lucide-react';

export const ProjectCockpitPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [updatingPlan, setUpdatingPlan] = useState(false);
  const [generatingBrief, setGeneratingBrief] = useState(false);
  const [promptInput, setPromptInput] = useState('');
  const [briefDialog, setBriefDialog] = useState(false);
  const [brief, setBrief] = useState('');
  const [copied, setCopied] = useState(false);
  const [expandedMilestones, setExpandedMilestones] = useState({});
  const [lastUpdate, setLastUpdate] = useState(null); // { summary, timestamp, type }
  const [scopeExpanded, setScopeExpanded] = useState(false);
  const [scopeDialog, setScopeDialog] = useState(false);

  useEffect(() => {
    loadProject();
  }, [id]);

  const loadProject = async () => {
    try {
      const res = await projectsAPI.getOne(id);
      setProject(res.data);
      // Expand all milestones by default
      if (res.data.execution_plan?.milestones) {
        const expanded = {};
        res.data.execution_plan.milestones.forEach(m => {
          expanded[m.id] = true;
        });
        setExpandedMilestones(expanded);
      }
    } catch (err) {
      setError('Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePlan = async () => {
    setGeneratingPlan(true);
    setError('');
    try {
      const res = await projectsAPI.generatePlan(id);
      setProject(res.data);
      
      // Track plan generation
      trackEvent('plan_generated', { project_id: id });
      
      // Expand all milestones
      if (res.data.execution_plan?.milestones) {
        const expanded = {};
        res.data.execution_plan.milestones.forEach(m => {
          expanded[m.id] = true;
        });
        setExpandedMilestones(expanded);
      }
      
      toast.success('Execution plan generated successfully');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate plan');
      toast.error('Failed to generate plan');
    } finally {
      setGeneratingPlan(false);
    }
  };

  const handleUpdatePlan = async () => {
    if (!promptInput.trim()) return;
    
    setUpdatingPlan(true);
    setError('');
    const submittedPrompt = promptInput;
    
    try {
      const res = await projectsAPI.updatePlan(id, promptInput);
      
      // New response format with change_summary
      const { project: updatedProject, change_summary, change_type } = res.data;
      
      setProject(updatedProject);
      setPromptInput('');
      
      // Track prompt update
      trackEvent('prompt_update_submitted', { 
        project_id: id, 
        change_type: change_type 
      });
      
      // Update last update message
      const timestamp = new Date().toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
      setLastUpdate({
        summary: change_summary,
        timestamp,
        type: change_type
      });
      
      // Show toast based on change type
      if (change_type === 'no_change') {
        toast.info(change_summary, {
          icon: <Info className="w-4 h-4" />,
        });
      } else {
        toast.success(change_summary, {
          icon: <CheckCircle2 className="w-4 h-4" />,
        });
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update plan');
      toast.error('Failed to update plan');
    } finally {
      setUpdatingPlan(false);
    }
  };

  const handleGenerateBrief = async () => {
    setGeneratingBrief(true);
    setError('');
    try {
      const res = await projectsAPI.generateBrief(id);
      setBrief(res.data.brief);
      setBriefDialog(true);
      
      // Track executive brief generation
      trackEvent('exec_brief_generated', { project_id: id });
      
      toast.success('Executive brief generated');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate brief');
      toast.error('Failed to generate brief');
    } finally {
      setGeneratingBrief(false);
    }
  };

  const handleCopyBrief = async () => {
    // Guard for empty/undefined brief
    if (!brief || typeof brief !== 'string' || brief.trim() === '') {
      toast.error('No content to copy');
      return;
    }

    // Helper: fallback copy using hidden textarea
    const fallbackCopy = () => {
      const textArea = document.createElement('textarea');
      textArea.value = brief;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      textArea.style.top = '-9999px';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      return successful;
    };

    try {
      let copied = false;

      // Try modern Clipboard API first (may fail on Safari/iOS)
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        try {
          await navigator.clipboard.writeText(brief);
          copied = true;
        } catch {
          // Clipboard API failed (Safari/iOS security), try fallback
          copied = fallbackCopy();
        }
      } else {
        // Clipboard API unavailable, use fallback
        copied = fallbackCopy();
      }

      if (copied) {
        setCopied(true);
        toast.success('Copied to clipboard');
        setTimeout(() => setCopied(false), 2000);
      } else {
        toast.error('Copy failed — please select manually');
      }
    } catch {
      // Catch any unexpected errors - no console.error to avoid DevTools noise
      toast.error('Copy failed — please select manually');
    }
  };

  const toggleMilestone = (milestoneId) => {
    setExpandedMilestones(prev => ({
      ...prev,
      [milestoneId]: !prev[milestoneId]
    }));
  };

  const plan = project?.execution_plan;

  // Get all tasks as flat array
  const allTasks = useMemo(() => {
    if (!plan?.tasks) return [];
    return Object.values(plan.tasks).flat();
  }, [plan]);

  // Group tasks by milestone
  const tasksByMilestone = useMemo(() => {
    if (!plan?.milestones || !allTasks.length) return {};
    
    const grouped = {};
    plan.milestones.forEach(m => {
      grouped[m.id] = allTasks.filter(t => t.related_milestone_id === m.id);
    });
    // Add unassigned tasks
    grouped['unassigned'] = allTasks.filter(t => !t.related_milestone_id);
    return grouped;
  }, [plan, allTasks]);

  // Calculate milestone progress and DERIVE status from tasks
  const milestoneProgress = useMemo(() => {
    if (!plan?.milestones) return {};
    
    const progress = {};
    plan.milestones.forEach(m => {
      const tasks = tasksByMilestone[m.id] || [];
      const total = tasks.length;
      const completed = tasks.filter(t => t.status === 'completed').length;
      
      // Derive status from task completion
      let derivedStatus;
      if (total === 0) {
        derivedStatus = 'not_started'; // No tasks linked
      } else if (completed === 0) {
        derivedStatus = 'not_started';
      } else if (completed === total) {
        derivedStatus = 'completed';
      } else {
        derivedStatus = 'in_progress';
      }
      
      progress[m.id] = {
        total,
        completed,
        percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
        status: derivedStatus
      };
    });
    return progress;
  }, [plan, tasksByMilestone]);

  // Calculate stats
  const totalTasks = allTasks.length;
  const completedTasks = allTasks.filter(t => t.status === 'completed').length;
  const activeBlockers = plan?.blockers?.filter(b => !b.resolved).length || 0;
  const pendingActions = plan?.pm_actions?.filter(a => !a.completed).length || 0;

  // Format start date for display
  const formatStartDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  // Get status badge styling
  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return { variant: 'default', className: 'bg-accent', label: 'Completed' };
      case 'in_progress':
        return { variant: 'secondary', className: '', label: 'In Progress' };
      case 'not_started':
      default:
        return { variant: 'outline', className: '', label: 'Not Started' };
    }
  };

  // Get milestone number circle styling
  const getMilestoneCircleClass = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-accent text-white';
      case 'in_progress':
        return 'bg-primary text-white';
      case 'not_started':
      default:
        return 'bg-slate-200 text-slate-600';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center py-20">
            <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h2 className="font-heading text-xl font-semibold mb-2">Project not found</h2>
            <Button onClick={() => navigate('/dashboard')} data-testid="back-btn">
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 animate-fade-in">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="mb-4"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
          
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div className="flex-1">
              <h1 className="font-heading text-3xl font-bold text-foreground" data-testid="project-title">
                {project.name}
              </h1>
              <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-muted-foreground">
                {project.start_date && (
                  <span className="flex items-center gap-1.5" data-testid="project-start-date">
                    <CalendarDays className="w-4 h-4" />
                    Started {formatStartDate(project.start_date)}
                  </span>
                )}
                <span className="flex items-center gap-1.5">
                  <Calendar className="w-4 h-4" />
                  {project.timeline}
                </span>
                <span className="flex items-center gap-1.5">
                  <Users className="w-4 h-4" />
                  {project.teams.join(', ')}
                </span>
              </div>
              
              {/* Project Scope Preview */}
              {project.scope && (
                <div className="mt-4" data-testid="project-scope-section">
                  <div className="flex items-start gap-2">
                    <FileSearch className="w-4 h-4 text-muted-foreground mt-1 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {project.scope}
                      </p>
                      {project.scope.length > 150 && (
                        <button 
                          onClick={() => setScopeDialog(true)}
                          className="text-xs text-primary hover:underline mt-1 flex items-center gap-1"
                          data-testid="view-scope-btn"
                        >
                          <Eye className="w-3 h-3" />
                          View full scope
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {!project.scope && (
                <p className="text-sm text-muted-foreground/60 mt-4 italic">No scope added yet</p>
              )}
            </div>
            
            <div className="flex flex-wrap gap-2">
              {!plan && (
                <Button 
                  onClick={handleGeneratePlan} 
                  disabled={generatingPlan}
                  data-testid="generate-plan-btn"
                >
                  {generatingPlan ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  {generatingPlan ? 'Generating...' : 'Generate Execution Plan'}
                </Button>
              )}
              {plan && (
                <Button 
                  variant="outline"
                  onClick={handleGenerateBrief} 
                  disabled={generatingBrief}
                  data-testid="generate-brief-btn"
                >
                  {generatingBrief ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <FileText className="w-4 h-4 mr-2" />
                  )}
                  {generatingBrief ? 'Generating...' : 'Executive Brief'}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 p-4 bg-destructive/10 text-destructive rounded-lg mb-6" data-testid="cockpit-error">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        {/* No Plan State */}
        {!plan && (
          <Card className="border-dashed border-2 border-slate-200 animate-fade-in">
            <CardContent className="py-16 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="font-heading font-semibold text-lg mb-2">No execution plan yet</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Generate an AI-powered execution plan to see milestones, tasks, blockers, and PM actions
              </p>
              <Button 
                onClick={handleGeneratePlan} 
                disabled={generatingPlan}
                data-testid="empty-generate-plan-btn"
              >
                {generatingPlan ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                Generate Execution Plan
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Cockpit Bento Grid */}
        {plan && (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 animate-fade-in">
            {/* Confidence Score */}
            <Card className="col-span-full md:col-span-3 border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                  Confidence Score
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center">
                  <div className="text-5xl font-heading font-bold text-primary" data-testid="confidence-score">
                    {plan.confidence_score}%
                  </div>
                  <Progress value={plan.confidence_score} className="mt-4 h-2" />
                  <p className="text-xs text-muted-foreground mt-2">
                    AI-assessed project clarity
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Prompt Bar */}
            <Card className="col-span-full md:col-span-9 border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-sm font-medium text-muted-foreground">
                  Update Plan with Natural Language
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <Input
                    placeholder='Try: "Mark API integration complete" or "Add security review before UAT"'
                    value={promptInput}
                    onChange={(e) => setPromptInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !updatingPlan) {
                        handleUpdatePlan();
                      }
                    }}
                    className="text-base"
                    disabled={updatingPlan}
                    data-testid="prompt-input"
                  />
                  <Button 
                    onClick={handleUpdatePlan} 
                    disabled={updatingPlan || !promptInput.trim()}
                    data-testid="send-prompt-btn"
                  >
                    {updatingPlan ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                
                {/* Last Update Message */}
                {lastUpdate && (
                  <div className="mt-3 flex items-start gap-2 text-sm" data-testid="last-update">
                    <div className={`flex items-center gap-1.5 ${
                      lastUpdate.type === 'no_change' ? 'text-amber-600' : 'text-accent'
                    }`}>
                      {lastUpdate.type === 'no_change' ? (
                        <Info className="w-3.5 h-3.5" />
                      ) : (
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      )}
                      <span className="font-medium">Last update:</span>
                    </div>
                    <span className="text-muted-foreground">{lastUpdate.summary}</span>
                    <span className="text-muted-foreground/60 ml-auto text-xs">{lastUpdate.timestamp}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Milestones with Tasks */}
            <Card className="col-span-full md:col-span-8 border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Target className="w-5 h-5 text-primary" />
                  Milestones & Tasks
                  <Badge variant="secondary" className="ml-auto">
                    {completedTasks}/{totalTasks} tasks
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[450px] pr-4">
                  <div className="space-y-4">
                    {plan.milestones?.map((milestone, index) => {
                      const progress = milestoneProgress[milestone.id] || { total: 0, completed: 0, percentage: 0, status: 'not_started' };
                      const tasks = tasksByMilestone[milestone.id] || [];
                      const isExpanded = expandedMilestones[milestone.id];
                      const statusBadge = getStatusBadge(progress.status);
                      
                      return (
                        <Collapsible 
                          key={milestone.id}
                          open={isExpanded}
                          onOpenChange={() => toggleMilestone(milestone.id)}
                        >
                          <div 
                            className="border border-slate-200 rounded-lg overflow-hidden"
                            data-testid={`milestone-${index}`}
                          >
                            {/* Milestone Header */}
                            <CollapsibleTrigger className="w-full">
                              <div className="flex items-center gap-3 p-4 bg-white hover:bg-slate-50 transition-colors cursor-pointer">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${getMilestoneCircleClass(progress.status)}`}>
                                  {index + 1}
                                </div>
                                <div className="flex-1 min-w-0 text-left">
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-medium text-foreground">{milestone.title}</h4>
                                    <Badge 
                                      variant={statusBadge.variant} 
                                      className={`${statusBadge.className} text-xs`}
                                      data-testid={`milestone-${index}-status`}
                                    >
                                      {statusBadge.label}
                                    </Badge>
                                  </div>
                                  <div className="flex items-center gap-4 mt-1">
                                    <span className="text-sm text-muted-foreground flex items-center gap-1">
                                      <Clock className="w-3.5 h-3.5" />
                                      {milestone.due_date}
                                    </span>
                                    <span className="text-sm text-muted-foreground">
                                      {progress.completed}/{progress.total} tasks
                                    </span>
                                  </div>
                                </div>
                                <div className="flex items-center gap-3">
                                  <div className="w-24">
                                    <div className="flex items-center justify-between text-xs mb-1">
                                      <span className="text-muted-foreground">Progress</span>
                                      <span className={`font-medium ${progress.percentage === 100 ? 'text-accent' : ''}`}>
                                        {progress.percentage}%
                                      </span>
                                    </div>
                                    <Progress value={progress.percentage} className="h-1.5" />
                                  </div>
                                  {isExpanded ? (
                                    <ChevronDown className="w-5 h-5 text-muted-foreground" />
                                  ) : (
                                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                                  )}
                                </div>
                              </div>
                            </CollapsibleTrigger>
                            
                            {/* Tasks under this milestone */}
                            <CollapsibleContent>
                              {tasks.length > 0 ? (
                                <div className="border-t border-slate-100 bg-slate-50 p-3">
                                  <div className="space-y-2">
                                    {tasks.map((task, taskIndex) => (
                                      <div 
                                        key={task.id}
                                        className="flex items-start gap-3 p-2 bg-white rounded border border-slate-100"
                                        data-testid={`milestone-${index}-task-${taskIndex}`}
                                      >
                                        <Checkbox 
                                          checked={task.status === 'completed'}
                                          className="mt-0.5"
                                          disabled
                                        />
                                        <div className="flex-1 min-w-0">
                                          <p className={`text-sm ${task.status === 'completed' ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                                            {task.title}
                                          </p>
                                          <div className="flex items-center gap-2 mt-1">
                                            <Badge variant="outline" className="text-xs">
                                              {task.category}
                                            </Badge>
                                            <Badge variant={
                                              task.priority === 'high' ? 'destructive' :
                                              task.priority === 'medium' ? 'secondary' : 'outline'
                                            } className="text-xs">
                                              {task.priority}
                                            </Badge>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ) : (
                                <div className="border-t border-slate-100 bg-slate-50 p-4 text-center">
                                  <p className="text-sm text-muted-foreground">No tasks linked to this milestone</p>
                                </div>
                              )}
                            </CollapsibleContent>
                          </div>
                        </Collapsible>
                      );
                    })}
                    
                    {/* Unassigned Tasks */}
                    {tasksByMilestone['unassigned']?.length > 0 && (
                      <Collapsible defaultOpen>
                        <div className="border border-dashed border-slate-300 rounded-lg overflow-hidden">
                          <CollapsibleTrigger className="w-full">
                            <div className="flex items-center gap-3 p-4 bg-white hover:bg-slate-50 transition-colors cursor-pointer">
                              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-slate-100 text-slate-500">
                                ?
                              </div>
                              <div className="flex-1 min-w-0 text-left">
                                <h4 className="font-medium text-muted-foreground">Unassigned Tasks</h4>
                                <p className="text-sm text-muted-foreground">
                                  {tasksByMilestone['unassigned'].length} tasks not linked to milestones
                                </p>
                              </div>
                              <ChevronDown className="w-5 h-5 text-muted-foreground" />
                            </div>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <div className="border-t border-slate-100 bg-slate-50 p-3">
                              <div className="space-y-2">
                                {tasksByMilestone['unassigned'].map((task, taskIndex) => (
                                  <div 
                                    key={task.id}
                                    className="flex items-start gap-3 p-2 bg-white rounded border border-slate-100"
                                  >
                                    <Checkbox 
                                      checked={task.status === 'completed'}
                                      className="mt-0.5"
                                      disabled
                                    />
                                    <div className="flex-1 min-w-0">
                                      <p className={`text-sm ${task.status === 'completed' ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                                        {task.title}
                                      </p>
                                      <div className="flex items-center gap-2 mt-1">
                                        <Badge variant="outline" className="text-xs">
                                          {task.category}
                                        </Badge>
                                        <Badge variant={
                                          task.priority === 'high' ? 'destructive' :
                                          task.priority === 'medium' ? 'secondary' : 'outline'
                                        } className="text-xs">
                                          {task.priority}
                                        </Badge>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </CollapsibleContent>
                        </div>
                      </Collapsible>
                    )}
                    
                    {(!plan.milestones || plan.milestones.length === 0) && (
                      <p className="text-muted-foreground text-center py-8">No milestones defined</p>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Right Column: Blockers + PM Actions */}
            <div className="col-span-full md:col-span-4 space-y-6">
              {/* Blockers */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                    Blockers
                    <Badge variant="destructive" className="ml-auto">
                      {activeBlockers}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[180px] pr-4">
                    <div className="space-y-3">
                      {plan.blockers?.map((blocker, index) => (
                        <div 
                          key={blocker.id}
                          className={`p-3 rounded-lg border ${
                            blocker.resolved 
                              ? 'bg-slate-50 border-slate-200 opacity-60' 
                              : blocker.severity === 'high' 
                                ? 'bg-red-50 border-red-200'
                                : blocker.severity === 'medium'
                                  ? 'bg-amber-50 border-amber-200'
                                  : 'bg-slate-50 border-slate-200'
                          }`}
                          data-testid={`blocker-${index}`}
                        >
                          <div className="flex items-start gap-2">
                            <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                              blocker.resolved ? 'bg-slate-400' :
                              blocker.severity === 'high' ? 'bg-red-500' :
                              blocker.severity === 'medium' ? 'bg-amber-500' : 'bg-slate-400'
                            }`} />
                            <div className="flex-1">
                              <p className={`text-sm ${blocker.resolved ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                                {blocker.description}
                              </p>
                              <div className="flex items-center gap-2 mt-2">
                                <Badge variant="outline" className="text-xs">
                                  {blocker.severity}
                                </Badge>
                                {blocker.resolved && (
                                  <Badge className="bg-accent text-xs">resolved</Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                      {(!plan.blockers || plan.blockers.length === 0) && (
                        <p className="text-muted-foreground text-center py-4">No blockers identified</p>
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* PM Actions */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <CheckSquare className="w-5 h-5 text-accent" />
                    PM Actions
                    <Badge variant="secondary" className="ml-auto">
                      {pendingActions} pending
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[180px] pr-4">
                    <div className="space-y-2">
                      {plan.pm_actions?.map((action, index) => (
                        <div 
                          key={action.id}
                          className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg"
                          data-testid={`pm-action-${index}`}
                        >
                          <Checkbox 
                            checked={action.completed}
                            className="mt-0.5"
                            disabled
                          />
                          <div className="flex-1">
                            <p className={`text-sm ${action.completed ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                              {action.action}
                            </p>
                            <Badge variant="outline" className="mt-1.5 text-xs">
                              {action.priority} priority
                            </Badge>
                          </div>
                        </div>
                      ))}
                      {(!plan.pm_actions || plan.pm_actions.length === 0) && (
                        <p className="text-muted-foreground text-center py-4">No PM actions defined</p>
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </main>

      {/* Executive Brief Dialog */}
      <Dialog open={briefDialog} onOpenChange={setBriefDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Executive Brief
            </DialogTitle>
            <DialogDescription>
              AI-generated summary for stakeholder communication
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 flex-1 overflow-hidden flex flex-col">
            <div className="max-h-[60vh] overflow-y-auto">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-foreground leading-relaxed whitespace-pre-wrap" data-testid="executive-brief-content">
                  {brief}
                </p>
              </div>
            </div>
            <div className="flex justify-end mt-4 pt-4 border-t">
              <Button onClick={handleCopyBrief} variant="outline" data-testid="copy-brief-btn">
                {copied ? (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" />
                    Copy to Clipboard
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Project Scope Dialog */}
      <Dialog open={scopeDialog} onOpenChange={setScopeDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <FileSearch className="w-5 h-5" />
              Project Scope
            </DialogTitle>
            <DialogDescription>
              Full project scope and objectives
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 flex-1 overflow-hidden flex flex-col">
            <div className="max-h-[60vh] overflow-y-auto">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-foreground leading-relaxed whitespace-pre-wrap" data-testid="full-scope-content">
                  {project?.scope || 'No scope defined'}
                </p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
